import { createRouter, createWebHistory } from "vue-router";

import firebase from "firebase/compat/app";
import "firebase/compat/auth";

import { captureException } from "@/posthog";
import { MODULE_IDS } from "@/utils/modules";
import { useUserStore } from "@/stores/user";
import { getEnvironment, isPublicDocumentPath, PRODUCTION_ORIGIN } from "@/utils/environment";

const routes = [
  {
    path: "/login",
    name: "Login",
    component: () => import("@/views/Login.vue"),
    meta: {
      title: "Connexion",
      forceDashboard: true,
    },
  },
  {
    path: "/login/application",
    name: "LoginApplication",
    component: () => import("@/views/LoginApplication.vue"),
    meta: {
      title: "Connecter une application",
      hideShell: true,
    },
  },
  {
    path: "/register",
    name: "Register",
    component: () => import("@/views/Register.vue"),
    meta: {
      title: "Inscription",
      forceDashboard: true,
    },
  },
  {
    path: "/",
    name: "dashboard",
    component: () => import("@/views/Dashboard.vue"),
    meta: {
      title: "Tableau de bord",
      requiresAuth: true,
      safePlace: true,
    },
  },
  {
    path: "/statistics",
    name: "Statistics",
    component: () => import("@/views/Statistics.vue"),
    meta: {
      title: "Statistiques",
      requiresAuth: true,
      safePlace: true,
    },
  },
  {
    path: "/profile",
    name: "Profile",
    component: () => import("@/views/Profile"),
    meta: {
      title: "Profil",
      requiresAuth: true,
      safePlace: true,
    },
  },
  {
    path: "/my-company",
    name: "Mon entreprise",
    component: () => import("@/views/MyCompany.vue"),
    meta: {
      title: "Mon entreprise",
      requiresAuth: true,
      safePlace: true,
    },
  },

  // MODULES
  {
    path: "/projects",
    name: "Projects",
    component: () => import("@/views/Projects"),
    meta: {
      title: "Projets",
      requiresAuth: true,
      module: MODULE_IDS.projects,
      safePlace: true,
    },
  },
  {
    path: "/team",
    name: "Team",
    component: () => import("@/views/Teams.vue"),
    meta: {
      title: "Équipes",
      requiresAuth: true,
      module: MODULE_IDS.teams,
      safePlace: true,
    },
  },
  {
    path: "/customers",
    name: "Customers",
    component: () => import("@/views/Customers.vue"),
    meta: {
      title: "Clients",
      requiresAuth: true,
      module: MODULE_IDS.customers,
      safePlace: true,
    },
  },
  {
    path: "/companies",
    name: "Companies",
    component: () => import("@/views/Companies.vue"),
    meta: {
      title: "Entreprises",
      requiresAuth: true,
      module: MODULE_IDS.companies,
      safePlace: true,
    },
  },
  {
    path: "/quotes",
    name: "Quotes",
    component: () => import("@/views/Quotes.vue"),
    meta: {
      title: "Devis",
      requiresAuth: true,
      module: MODULE_IDS.quotes,
      safePlace: true,
    },
  },
  {
    path: "/invoices",
    name: "Invoices",
    component: () => import("@/views/Invoices.vue"),
    meta: {
      title: "Factures",
      requiresAuth: true,
      module: MODULE_IDS.invoices,
      safePlace: false,
    },
  },
  {
    path: "/products",
    name: "Products",
    component: () => import("@/views/Products.vue"),
    meta: {
      title: "Produits",
      requiresAuth: true,
      module: MODULE_IDS.products,
      safePlace: true,
    },
  },
  {
    path: "/warehouses",
    redirect: "/logistics",
  },
  {
    path: "/logistics",
    name: "Logistics",
    component: () => import("@/views/Logistics.vue"),
    meta: {
      title: "Logistique",
      requiresAuth: true,
      module: MODULE_IDS.logistics,
      safePlace: true,
    },
  },
  {
    path: "/tools/:toolId?",
    name: "Tools",
    component: () => import("@/views/Tools.vue"),
    meta: {
      title: "Outils",
      requiresAuth: true,
      safePlace: true,
    },
  },
  {
    path: "/help",
    name: "Help",
    component: () => import("@/views/Help.vue"),
    meta: {
      title: "Aide",
      requiresAuth: true,
      safePlace: true,
    },
  },
  {
    path: "/help/:slug",
    name: "HelpArticle",
    component: () => import("@/views/Help.vue"),
    meta: {
      title: "Aide",
      requiresAuth: true,
      safePlace: true,
    },
    props: true,
  },
  {
    path: "/blogs",
    name: "Blogs",
    component: () => import(`@/views/Blogs.vue`),
    meta: {
      title: "Blogs",
      requiresAuth: true,
      module: MODULE_IDS.blogs,
      safePlace: true,
    },
  },
  {
    path: "/tasks",
    name: "Tasks",
    component: () => import(`@/views/Tasks.vue`),
    meta: {
      title: "Tâches",
      requiresAuth: true,
      safePlace: true,
    },
  },
  // ADMINISTRATION
  {
    path: "/administration",
    name: "Administration",
    component: () => import("@/views/administration/Dashboard.vue"),
    meta: {
      title: "Administration",
      adminOnly: true,
      requiresAuth: true,
      safePlace: true,
    },
  },
  {
    path: "/administration/catalog",
    name: "AdminCatalog",
    component: () => import("@/views/administration/Catalog.vue"),
    meta: {
      title: "Catalogue · Administration",
      adminOnly: true,
      requiresAuth: true,
      safePlace: true,
    },
  },
  {
    path: "/administration/notifications",
    name: "AdminNotifications",
    component: () => import("@/views/administration/Notifications.vue"),
    meta: {
      title: "Notifications · Administration",
      adminOnly: true,
      requiresAuth: true,
      safePlace: true,
    },
  },
  {
    path: "/administration/company/:id",
    name: "AdminCompany",
    component: () => import("@/views/administration/manage/Company.vue"),
    meta: {
      title: "Entreprise · Administration",
      adminOnly: true,
      requiresAuth: true,
      safePlace: true,
    },
    props: true,
  },
  {
    path: "/administration/customer/:id",
    name: "AdminCustomer",
    component: () => import("@/views/administration/manage/Customer.vue"),
    meta: {
      title: "Utilisateur · Administration",
      adminOnly: true,
      requiresAuth: true,
      safePlace: true,
    },
    props: true,
  },
  {
    path: "/administration/sandbox",
    name: "Sandbox",
    component: () => import("@/views/administration/Sandbox.vue"),
    meta: {
      title: "Sandbox · Administration",
      adminOnly: true,
      requiresAuth: true,
      safePlace: true,
    },
    props: true,
  },
  {
    path: "/administration/design-system",
    name: "DesignSystem",
    component: () => import("@/views/administration/DesignSystem.vue"),
    meta: {
      title: "Design System · Administration",
      adminOnly: true,
      requiresAuth: true,
      safePlace: true,
    },
  },
  // FICHES INDIVIDUELLES
  {
    path: "/record/company/:id",
    name: "Company",
    component: () => import("@/views/record/Company.vue"),
    meta: {
      title: "Entreprise",
      requiresAuth: true,
      module: MODULE_IDS.companies,
    },
    props: true,
  },
  {
    path: "/record/product/:id",
    name: "Product",
    component: () => import("@/views/record/Product.vue"),
    meta: {
      title: "Produit",
      requiresAuth: true,
      module: MODULE_IDS.products,
    },
    props: true,
  },
  {
    path: "/record/customer/:id",
    name: "Customer",
    component: () => import("@/views/record/Customer.vue"),
    meta: {
      title: "Client",
      requiresAuth: true,
      module: MODULE_IDS.customers,
    },
    props: true,
  },
  {
    path: "/record/quote/:id",
    name: "Quote",
    component: () => import("@/views/record/Quote.vue"),
    meta: {
      title: "Devis",
    },
    props: true,
  },
  {
    path: "/record/quote/:id/:companyId",
    name: "PublicQuote",
    component: () => import("@/views/record/Quote.vue"),
    meta: {
      title: "Devis",
      hideShell: true,
    },
    props: true,
  },
  {
    path: "/record/invoice/:id",
    name: "Invoice",
    component: () => import("@/views/record/Invoice.vue"),
    meta: {
      title: "Facture",
    },
    props: true,
  },
  {
    path: "/record/invoice/:id/:companyId",
    name: "PublicInvoice",
    component: () => import("@/views/record/Invoice.vue"),
    meta: {
      title: "Facture",
      hideShell: true,
    },
    props: true,
  },
  {
    path: "/record/project/:id",
    name: "Project",
    component: () => import(`@/views/record/Project.vue`),
    meta: {
      title: "Projet",
      requiresAuth: true,
      module: MODULE_IDS.projects,
    },
    props: true,
  },
  {
    path: "/record/warehouse/:warehouseId",
    name: "Warehouse",
    component: () => import(`@/views/record/Warehouse.vue`),
    meta: {
      title: "Entrepôt",
      requiresAuth: true,
      module: MODULE_IDS.logistics,
    },
    props: true,
  },
  {
    path: "/record/selling-point/:id",
    name: "SellingPoint",
    component: () => import(`@/views/record/SellingPoint.vue`),
    meta: {
      title: "Point de vente",
      requiresAuth: true,
      module: MODULE_IDS.logistics,
    },
    props: true,
  },

  {
    path: "/blog/:id",
    name: "Blog",
    component: () => import(`@/views/record/Blog.vue`),
    meta: {
      title: "Blog",
      requiresAuth: true,
      module: MODULE_IDS.blogs,
    },
    props: true,
  },
  {
    path: "/blog/:id/add",
    name: "AddBlog",
    component: () => import(`@/views/blog/Add.vue`),
    meta: {
      title: "Nouvel article",
      requiresAuth: true,
      module: MODULE_IDS.blogs,
    },
    props: true,
  },
  {
    path: "/blog/:id/add/:modArticle",
    name: "ModifyArticle",
    component: () => import(`@/views/blog/Add.vue`),
    meta: {
      title: "Modifier l'article",
      requiresAuth: true,
      module: MODULE_IDS.blogs,
    },
    props: true,
  },
  {
    path: "/blog/:id/articles",
    redirect: (to) => ({ name: "Blog", params: { id: to.params.id } }),
  },
  {
    path: "/blog/:id/article/:idArticle",
    name: "Article",
    component: () => import(`@/views/record/Article.vue`),
    meta: {
      title: "Article",
      requiresAuth: true,
      module: MODULE_IDS.blogs,
    },
    props: true,
  },

  // STRIPE
  {
    path: "/stripe/checkout/:company/:session",
    name: "PageCheckoutSessionSuccess",
    component: () => import(`@/views/stripe/CheckoutSessionSuccess.vue`),
    meta: {
      title: "Confirmation de votre achat",
      requiresAuth: false,
    },
    props: true,
  },
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    component: () => import("@/views/NotFound.vue"),
    meta: {
      title: "Page introuvable",
    },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL || "/"),
  routes,
});

router.onError((error) => {
  captureException(error, {
    flow: "vue_router",
  });
});

router.beforeEach(async (to) => {
  const isAuthenticated = firebase.auth().currentUser;

  const requiresAuth = to.meta.requiresAuth ?? false;
  const forceDashboard = to.meta.forceDashboard ?? false;

  if (isAuthenticated && getEnvironment() && !isPublicDocumentPath(to.path)
    && !(await useUserStore().checkBetaAccess(isAuthenticated.uid))) {
    window.location.replace(`${PRODUCTION_ORIGIN}${to.fullPath}`);
    return false;
  }

  if (requiresAuth && !isAuthenticated)
    return "/login";
  if (forceDashboard && isAuthenticated)
    return "/";
  if (to.meta.adminOnly && !(await useUserStore().checkAdmin()))
    return "/";
});

export default router;
